// FAQSection.js
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus } from 'lucide-react';

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: 'Can you guarantee me a job?',
      answer: 'No, and no one honestly can. What we do is open access to interview opportunities for candidates who meet the criteria, using the employer relationships we have built. Our part is preparing a strong application, arranging the interview, and standing behind you while the process runs. The decision itself belongs entirely to the employer, judged against the standards and criteria they apply. Being well prepared and put in front of a legitimate opportunity is what we can offer; hiring you is their call.'
    },
    {
      question: 'Am I certain to get an interview?',
      answer: 'Not automatically. Candidates placed in the Work Abroad pipeline are put forward for interview slots with partner employers, but that always depends on meeting the requirements of a particular role and the criteria the employer sets. How soon a slot appears is shaped by employer hiring cycles, how much recruiting they are doing at the time, and whether you are ready to be presented. We push to secure those opportunities for candidates who qualify, though the timing is not ours to control. And an interview is not an offer: employers decide on the basis of how it goes and the selection criteria they use.'
    },
    {
      question: 'Which roles can you help with?',
      answer: 'Most of our work sits in care and healthcare support, alongside a selection of skilled and semi-skilled positions across other industries. What is actually open at any moment turns on three things: what employers are recruiting for, the rules in the destination country, and the profiles of the candidates we have. Your eligibility consultation is where we go through which of those roles line up with your experience, your qualifications and where you want your career to go. We put people forward where their background gives them the strongest chance, within whatever our employer partners have available.'
    },
    {
      question: 'What does the service cost?',
      answer: 'There is a service fee, and it applies once you move past the initial assessment and into the Work Abroad pipeline. It is charged at the point of placement. Covered by it: screening for eligibility, matching you to roles, coordinating interviews with employers, preparation material and coaching, guidance on documentation, and support that carries on while you are being hired. The first eligibility conversation exists so you can weigh up your options before any financial commitment arises, and we go through every charge openly during it.'
    },
    {
      question: 'Where does the money actually go?',
      answer: 'You never pay Ivipass for a job, for wages, or for anything an employer would normally charge. Our fee is for professional support alone: preparation, coordination and guidance as the process runs. Employment contracts, salary payments, visa fees and every other employer-related cost are settled directly between you and the employer, or with the relevant authority. We are not a middleman for employment payments. Each financial arrangement tied to the job itself sits between you and the company you work for, which keeps that relationship clear and secure.'
    },
    {
      question: 'What if an interview does not go my way?',
      answer: 'We will sit down with you, work out what happened and share whatever constructive feedback we are able to. Whether further opportunities are worth pursuing depends on your profile, on what employers have open at that moment, on how ready you are for a different role, and on what came back from the interview. It is judged case by case rather than promised in advance. We stay with candidates through the whole journey, and part of that is being straight with you about what a realistic next step looks like.'
    },
    {
      question: 'Do you deal with visas?',
      answer: 'Guidance and preparation, yes, as part of the wider service. That means explaining which documents are needed for employment and for the visa, helping you get those materials together, and talking you through how the application works. What we cannot do is decide the outcome. Visas are processed and granted or refused by immigration authorities and government agencies applying their own rules and criteria, and nobody outside those bodies can influence or promise a result. Our job is making sure you enter the process prepared and properly informed.'
    },
    {
      question: 'Do I deal with the employer myself?',
      answer: 'Yes. From the moment interviews are booked and recruitment gets underway, the formal side runs straight between you and the employer: interview invitations, job offers, contract negotiation, salary discussions and joining instructions. Official documents and correspondence come from them to you, in your own name. We stay alongside all of it, ready to explain anything you receive, suggest how to respond, and support you as it unfolds. Think of us as your adviser and backup rather than a channel that official employment communication has to pass through.'
    },
    {
      question: 'I am not sure I would even qualify.',
      answer: 'That is precisely what the eligibility consultation is there to settle. In that first session, which carries no charge, we work carefully through your employment history, your qualifications and certifications, how ready your documents are, and anything else bearing on whether you can work abroad. Then you get an honest read: how realistic your chances look, which roles might suit you, and what would strengthen your profile if something needs shoring up. The aim is a clear and truthful picture up front, so you can decide about working abroad without surprises appearing later. Nothing obliges you to go further afterwards.'
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-16 bg-surface">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-semibold text-primary mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-gray-600 text-sm font-light max-w-2xl mx-auto">
            Straight answers to what candidates ask us most about working abroad
          </p>
        </motion.div>

        {/* FAQ Accordion */}
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.05 }}
              className="border border-gray-200 rounded-lg overflow-hidden bg-white"
            >
              {/* Question Button */}
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full flex items-center justify-between p-6 text-left bg-white hover:bg-surface transition-colors"
              >
                <span className="text-base font-semibold text-primary pr-4">
                  {faq.question}
                </span>
                <div className="flex-shrink-0">
                  {openIndex === index ? (
                    <Minus size={20} className="text-primary" />
                  ) : (
                    <Plus size={20} className="text-primary" />
                  )}
                </div>
              </button>

              {/* Answer */}
              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="p-6 pt-0 bg-white">
                      <p className="text-gray-600 text-sm font-light leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
